import { z } from 'zod';
import type { RecipeSummary } from './api/recipes.ts';
import { DIETS, MEAL_TYPES, type Diet, type MealType } from './recipe.ts';

export const RECIPE_SORTS = ['new', 'rating', 'last_cooked', 'protein', 'cost', 'time'] as const;
export type RecipeSort = (typeof RECIPE_SORTS)[number];

const list = <T extends string>(values: readonly [T, ...T[]]) =>
  z
    .union([z.array(z.enum(values)), z.string()])
    .transform((v) =>
      (Array.isArray(v) ? v : v.split(',')).filter((x): x is T =>
        (values as readonly string[]).includes(x),
      ),
    );
const optionalNumber = z.coerce.number().positive().optional().catch(undefined);
const flag = z
  .union([z.boolean(), z.string()])
  .transform((v) => v === true || v === '1' || v === 'true')
  .optional();

/** Recipe list filters (URL search params and `GET /api/recipes`). */
export const recipeFiltersSchema = z.object({
  q: z.string().trim().max(200).optional().catch(undefined),
  mealType: list(MEAL_TYPES as unknown as readonly [MealType, ...MealType[]])
    .optional()
    .catch(undefined),
  diet: list(DIETS as unknown as readonly [Diet, ...Diet[]])
    .optional()
    .catch(undefined),
  /** Total time at most, minutes. */
  maxMinutes: optionalNumber,
  maxKcal: optionalNumber,
  maxCost: optionalNumber,
  mealPrep: flag,
  freezer: flag,
  favourite: flag,
  sort: z.enum(RECIPE_SORTS).optional().catch(undefined),
});
export type RecipeFilters = z.infer<typeof recipeFiltersSchema>;

const minutesOf = (r: RecipeSummary) => r.totalMin ?? ((r.prepMin ?? 0) + (r.cookMin ?? 0) || null);

/** Whether a recipe passes the facet filters (text search is separate). */
export function matchesFilters(r: RecipeSummary, f: RecipeFilters): boolean {
  if (f.mealType?.length && !f.mealType.some((t) => r.mealTypes.includes(t))) return false;
  // Diets narrow: a vegan and gluten-free search needs both.
  if (f.diet?.length && !f.diet.every((d) => r.diets.includes(d))) return false;
  if (f.maxMinutes !== undefined) {
    const minutes = minutesOf(r);
    if (minutes === null || minutes > f.maxMinutes) return false;
  }
  if (f.maxKcal !== undefined && (r.kcalPerPortion === null || r.kcalPerPortion > f.maxKcal)) {
    return false;
  }
  if (f.maxCost !== undefined && (r.costPerPortion === null || r.costPerPortion > f.maxCost)) {
    return false;
  }
  if (f.mealPrep && !r.mealPrepFriendly) return false;
  if (f.freezer && !r.freezerFriendly) return false;
  if (f.favourite && !r.favourite) return false;
  return true;
}

/** Nulls last, whatever the direction. */
function byValue<T>(get: (r: T) => number | string | null, desc: boolean) {
  return (a: T, b: T) => {
    const x = get(a);
    const y = get(b);
    if (x === y) return 0;
    if (x === null) return 1;
    if (y === null) return -1;
    return (x < y ? -1 : 1) * (desc ? -1 : 1);
  };
}

export function sortRecipes(recipes: RecipeSummary[], sort: RecipeSort = 'new'): RecipeSummary[] {
  const compare = {
    new: byValue<RecipeSummary>((r) => r.createdAt, true),
    rating: byValue<RecipeSummary>((r) => r.rating, true),
    last_cooked: byValue<RecipeSummary>((r) => r.lastCookedOn, true),
    protein: byValue<RecipeSummary>((r) => r.proteinPerPortion, true),
    cost: byValue<RecipeSummary>((r) => r.costPerPortion, false),
    time: byValue<RecipeSummary>(minutesOf, false),
  }[sort];
  // Newest first among equals.
  const newest = byValue<RecipeSummary>((r) => r.createdAt, true);
  return [...recipes].sort((a, b) => compare(a, b) || newest(a, b));
}

/** Facet counts for the filter sheet ("Meal prep 14"), over an already searched list. */
export function facetCounts(recipes: RecipeSummary[]) {
  const count = (pred: (r: RecipeSummary) => boolean) => recipes.filter(pred).length;
  return {
    mealType: Object.fromEntries(
      MEAL_TYPES.map((t) => [t, count((r) => r.mealTypes.includes(t))]),
    ) as Record<MealType, number>,
    diet: Object.fromEntries(DIETS.map((d) => [d, count((r) => r.diets.includes(d))])) as Record<
      Diet,
      number
    >,
    mealPrep: count((r) => r.mealPrepFriendly),
    freezer: count((r) => r.freezerFriendly),
    favourite: count((r) => r.favourite),
  };
}

/** Filters that are set (for chips and "reset"). */
export function activeFilterCount(f: RecipeFilters): number {
  return (
    (f.mealType?.length ?? 0) +
    (f.diet?.length ?? 0) +
    [f.maxMinutes, f.maxKcal, f.maxCost].filter((v) => v !== undefined).length +
    [f.mealPrep, f.freezer, f.favourite].filter(Boolean).length
  );
}
